import { SCOPE_LABEL } from '@locales/id/labels';
import type { Scope } from '@lib/api/common';

// Official region names (Kemendagri) shortened for compact places (map panel, cards): the full name stays in forms.

const REGENCY_RULES: [RegExp, string][] = [
  // Jakarta's five cities are known by their own name
  [/^Kota Administrasi\s+/i, ''],
  [/^Kabupaten( Administrasi)?\s+/i, 'Kab. '],
];
const PROVINCE_RULES: [RegExp, string][] = [
  [/^Daerah Khusus Ibukota\s+/i, 'DKI '],
  [/^Daerah Khusus\s+/i, 'DK '],
  [/^Daerah Istimewa\s+/i, 'DI '],
];
const shorten = (name: string, rules: [RegExp, string][]) => rules.reduce((n, [re, to]) => n.replace(re, to), name.trim());

/** "Kota Administrasi Jakarta Selatan" -> "Jakarta Selatan", "Kabupaten Bogor" -> "Kab. Bogor", "Kota Bogor" stays. */
export const shortRegency = (name: string) => shorten(name, REGENCY_RULES);
/** "Daerah Khusus Ibukota Jakarta" -> "DKI Jakarta", "Daerah Istimewa Yogyakarta" -> "DI Yogyakarta". */
export const shortProvince = (name: string) => shorten(name, PROVINCE_RULES);

/**
 * One line for where a location is: "Pasar Minggu, Jakarta Selatan · DKI Jakarta". The regency is left out of the
 * address part when the address already names it.
 */
export function placeLine(l: { address: string | null; regencyName: string; provinceName: string }): string {
  const regency = shortRegency(l.regencyName);
  const province = shortProvince(l.provinceName);
  const address = l.address?.trim();
  if (!address) return `${regency} · ${province}`;
  const named = address.toLowerCase().includes(regency.replace(/^Kab\. /, '').toLowerCase());
  return named ? `${address} · ${province}` : `${address}, ${regency} · ${province}`;
}

/**
 * Where an account's access reaches, in a few words: "Nasional", "Jawa Barat", "Hub Desa Ragunan · DKI Jakarta" (a
 * location with its province, so two hubs of the same name stay apart).
 */
export function scopeText(s: Scope): string {
  if (s.kind === 'national') return SCOPE_LABEL.national;
  if (s.kind === 'province') return shortProvince(s.provinceName);
  return `${s.locationName} · ${shortProvince(s.provinceName)}`;
}
